/**
 * @Module: useRecipeById Hook
 * @Status: Ready
 * @Summary: Resolves a recipe by ID from Firestore, localStorage, or sample data
 * @Description: Single source of truth for loading one recipe. Checks in order:
 *   1. Firestore (if user signed in + Firestore available)
 *   2. localStorage saved recipes
 *   3. Sample/demo recipes
 *   Returns loading/error/recipe state. Used by RecipeDetailScreen and CookScreen.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isFirebaseConfigured, db } from '../config/firebase';
import { createRecipeService } from '../services/firestore/recipe.service';
import { getSampleRecipe, sampleRecipes } from '../data/sampleRecipes';
import type { Recipe } from '../types/recipe';
import type { StoredRecipe } from '../types/stored-recipe';

const LOCAL_STORAGE_KEY = 'choppy:recipes';

function loadLocalRecipes(): StoredRecipe[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredRecipe[]) : [];
  } catch {
    return [];
  }
}

interface UseRecipeByIdReturn {
  recipe: Recipe | null;
  loading: boolean;
  error: string | null;
  source: 'firestore' | 'local' | 'sample' | null;
}

export function useRecipeById(recipeId: string | undefined): UseRecipeByIdReturn {
  const { user } = useAuth();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<UseRecipeByIdReturn['source']>(null);

  useEffect(() => {
    if (!recipeId) {
      setRecipe(null);
      setLoading(false);
      setError('No recipe ID');
      return;
    }

    let cancelled = false;

    async function resolve() {
      setLoading(true);
      setError(null);

      // 1. Try Firestore (if signed in and configured)
      if (user?.uid && isFirebaseConfigured() && db) {
        try {
          const service = createRecipeService(db);
          const stored = await service.get(user.uid, recipeId!);
          if (!cancelled) {
            setRecipe(stored);
            setSource('firestore');
            setLoading(false);
            return;
          }
        } catch {
          // Not found in Firestore — fall through to other sources
        }
      }

      // 2. Try localStorage
      const local = loadLocalRecipes().find(r => r.id === recipeId);
      if (local) {
        if (!cancelled) {
          setRecipe(local);
          setSource('local');
          setLoading(false);
          return;
        }
      }

      // 3. Try sample recipes
      const sample = getSampleRecipe(recipeId!);
      if (sample) {
        if (!cancelled) {
          setRecipe(sample);
          setSource('sample');
          setLoading(false);
          return;
        }
      }

      // Not found anywhere
      if (!cancelled) {
        setRecipe(null);
        setSource(null);
        setError(`Recipe "${recipeId}" not found`);
        setLoading(false);
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [recipeId, user?.uid]);

  return { recipe, loading, error, source };
}

/** Get all recipes — merged from Firestore/localStorage + samples */
export function getAllRecipes(
  savedRecipes: StoredRecipe[],
): Recipe[] {
  // Saved recipes first (newest), then samples that aren't duplicated
  const savedIds = new Set(savedRecipes.map(r => r.id));
  const samples = sampleRecipes.filter(r => !savedIds.has(r.id));
  return [...savedRecipes, ...samples];
}
